import { Resources } from 'constants/resources';

import { useState } from 'react';

import { archiveCompany } from 'config/requests/companies';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { queryClient } from 'providers/queryClient';
import { useNotify } from 'react-admin';
import { Button } from 'shared/mui/Buttons';
import { CardActionsButtons, CardHeader } from 'shared/mui/Card';
import { CardContent } from 'shared/mui/Card/CardContent/CardContent';
import { Dialog } from 'shared/mui/Dialogs';
import { CloseIconButton } from 'shared/mui/IconButtons';
import { Typography } from 'shared/mui/Typography';
import { TypographyBold } from 'shared/mui/Typography/TypographyBold';
import { Company } from 'types/embedded/company';

type CompaniesUnarchiveDialogProps = {
  record: Company;
  open: boolean;
  onClose: VoidFunction;
};

export const CompaniesUnarchiveDialog: React.FC<CompaniesUnarchiveDialogProps> = ({
  record,
  open,
  onClose,
}) => {
  const translate = useTypedTranslate();
  const [isLoading, setIsLoading] = useState(false);
  const notify = useNotify();

  const onArchive = async () => {
    try {
      setIsLoading(true);
      await archiveCompany(record.id, false);
      setIsLoading(false);
      notify(
        <>
          Компания <TypographyBold text={record?.name || ''} /> восстановлена
        </>,
        { type: 'success' },
      );
      onClose();
      await queryClient.refetchQueries({ queryKey: [Resources.Companies] });
    } catch (e) {
      notify((e as Error)?.message || translate('app.common.error'), { type: 'error' });
      setIsLoading(false);
    }
  };

  return (
    <Dialog onClose={onClose} open={open} width="640px">
      <CardHeader
        appendActions={[<CloseIconButton key="close-button" onClose={onClose} />]}
        title={translate('app.companies.companyUnarchiveTitle')}
        titleSize="smallBold"
      />
      <CardContent>
        <Typography>
          {translate('app.companies.companyActivated1')}{' '}
          <TypographyBold fontSize={16} text={record?.name || ''} />{' '}
          {translate('app.companies.companyActivated2')}
        </Typography>
      </CardContent>
      <CardActionsButtons
        rightActionsSlot={[
          <Button
            key="close-button"
            label={translate('app.buttons.cancel')}
            onClick={onClose}
            variant="outlined"
          />,
          <Button
            isLoading={isLoading}
            key="archive-button"
            label={translate('app.buttons.restore')}
            onClick={onArchive}
            variant="contained"
          />,
        ]}
      />
    </Dialog>
  );
};
